import { useState } from 'react'
import toast from 'react-hot-toast'
import Modal from '../../ui/Modal'
import { deliveriesAPI } from '../../../api/deliveries'

const RECLAMATION_TYPES = [
  { value: 'lost',          label: '📦 Colis perdu' },
  { value: 'damaged',       label: '💔 Colis endommagé' },
  { value: 'late',          label: '⏰ Livraison trop tardive' },
  { value: 'wrong_address', label: '🗺️ Mauvaise adresse de livraison' },
  { value: 'other',         label: '❓ Autre' },
]

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13,
  fontWeight: 600, color: '#374151', marginBottom: 8,
}

const textareaStyle: React.CSSProperties = {
  width: '100%', padding: '12px 14px',
  borderRadius: 12, border: '1.5px solid #E5E7EB',
  fontSize: 14, fontFamily: "'DM Sans', sans-serif",
  resize: 'vertical', outline: 'none',
  background: '#FAFAFA', color: '#0D1117',
  boxSizing: 'border-box',
}

const btnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  border: 'none', borderRadius: 12,
  padding: '12px 20px', color: '#fff',
  fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
  flex: 1, justifyContent: 'center',
}

interface Props {
  deliveryId: string
  trackingCode: string
  onClose: () => void
  onSuccess: () => void
}

export const ExpReclamationModal = ({ deliveryId, trackingCode, onClose, onSuccess }: Props) => {
  const [reclamType, setReclamType] = useState('')
  const [description, setDescription] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!reclamType) {
      setError('Veuillez sélectionner un type de réclamation')
      return
    }
    if (description.trim().length < 10) {
      setError('La description doit contenir au moins 10 caractères')
      return
    }

    setIsLoading(true)
    setError('')
    try {
      await deliveriesAPI.addReclamation(deliveryId, {
        type: reclamType,
        description: description.trim(),
      })
      toast.success('Réclamation envoyée — notre équipe vous contactera sous 24h')
      onSuccess()
      onClose()
    } catch {
      setError("Erreur lors de l'envoi de la réclamation")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open title="Ouvrir une réclamation" onClose={onClose} size="sm">
      <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20, marginTop: 0 }}>
        Colis {trackingCode} · Notre équipe vous contactera sous 24h.
      </p>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Type de problème *</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {RECLAMATION_TYPES.map((t) => (
            <label key={t.value} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              border: `1px solid ${reclamType === t.value ? '#F59E0B' : '#E5E7EB'}`,
              background: reclamType === t.value ? '#FFFBEB' : '#fff',
              cursor: 'pointer', fontSize: 14,
            }}>
              <input
                type="radio" name="reclamType" value={t.value}
                checked={reclamType === t.value}
                onChange={() => { setReclamType(t.value); setError('') }}
                style={{ accentColor: '#F59E0B' }}
              />
              {t.label}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Description détaillée *</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Décrivez le problème en détail : date, circonstances, dommages constatés..."
          rows={5} maxLength={1000}
          style={textareaStyle}
        />
        <div style={{ textAlign: 'right', fontSize: 11, color: '#9CA3AF' }}>
          {description.length}/1000 (min 10)
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px', color: '#EF4444', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ ...btnStyle, background: '#F3F4F6', color: '#374151' }}>
          Annuler
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          style={{ ...btnStyle, background: '#F59E0B', opacity: isLoading ? 0.7 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }}
        >
          {isLoading ? 'Envoi...' : 'Envoyer la réclamation'}
        </button>
      </div>
    </Modal>
  )
}
